import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik, useFormikContext } from "formik";

import {
  Button,
  TextField as MuiTextField,
  Alert,
  AlertTitle,
  Link as MuiLink,
} from "@mui/material";
import { Box, spacing } from "@mui/system";

import useAuth from "../../hooks/useAuth";
import { getRoadInfo } from "../../services/vayvaService";

const UserRoot = styled.div`
  max-width: 540px;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 100%;
  flex-direction: column;
`;

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

interface Step1Props {
  currentStep: number;
}

const SUPPORT_URL = "https://tiehoitokunta.fi/yhteys";
const supportLink = (
  <MuiLink href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
    tiehoitokunta.fi/yhteys
  </MuiLink>
);

const ErrorAutoClear: React.FC<{ onClear: () => void }> = ({ onClear }) => {
  const { values } = useFormikContext<any>();
  const prev = React.useRef(values);

  React.useEffect(() => {
    if (prev.current !== values) {
      onClear();
      prev.current = values;
    }
  }, [values, onClear]);

  return null;
};

const makeError = (title: string, body: string) => (
  <>
    <AlertTitle>{title}</AlertTitle>
    {body} Ota yhteyttä {supportLink}.
  </>
);

const ERRORS = {
  roadInvalid: makeError(
    "Tien tiedot eivät kelpaa",
    "Tarkista tien nimi ja kunta."
  ),
  roadExists: makeError(
    "Tie on jo käytössä",
    "Jos sinun tulisi hallinnoida tätä tietä, ole yhteydessä tukeen."
  ),
  userExists: makeError(
    "Sähköpostiosoite on jo rekisteröity",
    "Voit kirjautua sisään tai palauttaa salasanan."
  ),
  server: makeError("Järjestelmävirhe", "Yritä hetken kuluttua uudelleen."),
  network: makeError(
    "Rekisteröinti epäonnistui",
    "Tarkista verkkoyhteytesi ja yritä uudelleen."
  ),
};

export const Step1: React.FC<Step1Props> = ({ currentStep }) => {
  if (currentStep !== 1) return null;

  return (
    <>
      <Field name="firstName" my={3}>
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Etunimi"
            variant="outlined"
            fullWidth
            error={!!form.errors.firstName && form.touched.firstName}
            helperText={<ErrorMessage name="firstName" />}
            my={2}
          />
        )}
      </Field>
      <Field name="lastName">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Sukunimi"
            variant="outlined"
            fullWidth
            error={!!form.errors.lastName && form.touched.lastName}
            helperText={<ErrorMessage name="lastName" />}
            my={2}
          />
        )}
      </Field>
      <Field name="email">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Sähköposti"
            variant="outlined"
            fullWidth
            error={!!form.errors.email && form.touched.email}
            helperText={<ErrorMessage name="email" />}
            my={2}
          />
        )}
      </Field>
      <Field name="phone">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Puhelinnumero"
            variant="outlined"
            fullWidth
            error={!!form.errors.phone && form.touched.phone}
            helperText={<ErrorMessage name="phone" />}
            my={2}
          />
        )}
      </Field>
      <Field name="password">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Salasana"
            variant="outlined"
            type="password"
            fullWidth
            error={!!form.errors.password && form.touched.password}
            helperText={<ErrorMessage name="password" />}
            my={2}
          />
        )}
      </Field>
      <Field name="passwordConfirmation">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Vahvista salasana"
            variant="outlined"
            type="password"
            fullWidth
            error={
              !!form.errors.passwordConfirmation &&
              form.touched.passwordConfirmation
            }
            helperText={<ErrorMessage name="passwordConfirmation" />}
            my={2}
          />
        )}
      </Field>
      <Field name="termsAccepted" type="checkbox">
        {({ field, form }: any) => (
          <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
            <label>
              <input
                {...field}
                type="checkbox"
                checked={field.value}
                style={{ marginRight: "0.5rem" }}
              />
              Hyväksyn{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "underline" }}
              >
                käyttöehdot ja tietosuojaselosteen
              </a>
            </label>
            {form.errors.termsAccepted && form.touched.termsAccepted && (
              <p style={{ color: "#d32f2f", fontSize: "0.8rem" }}>
                {form.errors.termsAccepted}
              </p>
            )}
          </div>
        )}
      </Field>
    </>
  );
};

interface Step2Props {
  currentStep: number;
}

export const Step2: React.FC<Step2Props> = ({ currentStep }) => {
  if (currentStep !== 2) return null;

  return (
    <>
      <Field name="streetName" my={3}>
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Tien nimi"
            variant="outlined"
            fullWidth
            error={!!form.errors.streetName && form.touched.streetName}
            helperText={<ErrorMessage name="streetName" />}
            my={2}
          />
        )}
      </Field>
      <Field name="municipality">
        {({ field, form }: any) => (
          <TextField
            {...field}
            label="Kunta"
            variant="outlined"
            fullWidth
            error={!!form.errors.municipality && form.touched.municipality}
            helperText={<ErrorMessage name="municipality" />}
            my={2}
          />
        )}
      </Field>
    </>
  );
};

function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp } = useAuth();

  const [success, setSuccess] = useState<boolean>(false);
  const [membership, setMembership] = useState<string>("basic");
  const [error, setError] = useState<React.ReactNode>(null);

  const validationSchema = [
    Yup.object({
      firstName: Yup.string().trim().required("Etunimi on pakollinen"),
      lastName: Yup.string().trim().required("Sukunimi on pakollinen"),
      email: Yup.string()
        .trim()
        .email("Virheellinen sähköpostiosoite")
        .required("Sähköposti on pakollinen"),
      phone: Yup.string()
        .trim()
        .matches(/^\+?[0-9\s\-()]{7,20}$/, "Virheellinen puhelinnumero")
        .required("Puhelinnumero on pakollinen"),
      password: Yup.string()
        .min(6, "Salasanan tulee olla vähintään 6 merkkiä pitkä")
        .required("Salasana on pakollinen"),
      passwordConfirmation: Yup.string()
        .oneOf([Yup.ref("password"), null], "Salasanat eivät täsmää")
        .required("Salasanan vahvistaminen on pakollinen"),
      termsAccepted: Yup.boolean()
        .oneOf([true], "Sinun tulee hyväksyä käyttöehdot ja tietosuojaseloste")
        .required("Sinun tulee hyväksyä käyttöehdot ja tietosuojaseloste"),
    }),
    Yup.object({
      streetName: Yup.string().trim().required("Tien nimi on pakollinen"),
      municipality: Yup.string().trim().required("Kunta on pakollinen"),
    }),
  ];

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    passwordConfirmation: "",
    streetName: "",
    municipality: "",
    termsAccepted: false,
  };

  const [currentStep, setCurrentStep] = useState(1);

  const isLastStep = currentStep === validationSchema.length;

  const handleSubmit = async (values: any, actions: any) => {
    if (isLastStep) {
      try {
        // Trim values before sending to API
        const streetName = values.streetName?.trim() || "";
        const municipality = values.municipality?.trim() || "";
        const firstName = values.firstName?.trim() || "";
        const lastName = values.lastName?.trim() || "";
        const email = values.email?.trim() || "";
        const phone = values.phone?.trim() || "";

        const res = await getRoadInfo(streetName, municipality);
        const isValidRoad = !res.data.features[0].properties.virheet;

        if (isValidRoad) {
          await signUp({
            email,
            password: values.password,
            firstName,
            lastName,
            phone,
            streetName,
            municipality,
            membership: membership,
          });
          setSuccess(true);
        } else {
          setError(ERRORS.roadInvalid);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;

        if (typeof errorMessage === "string") {
          if (errorMessage.includes("Road already exists")) {
            setError(ERRORS.roadExists);
          } else if (errorMessage.includes("User already exists")) {
            setError(ERRORS.userExists);
          } else {
            setError(ERRORS.server);
          }
        } else {
          setError(ERRORS.network);
        }
      } finally {
        actions.setSubmitting(false);
      }
    } else {
      setCurrentStep((prevStep) => prevStep + 1);
    }
    actions.setTouched({});
    actions.setSubmitting(false);
  };

  const prevStep = () => setCurrentStep((prevStep) => prevStep - 1);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const membershipParam = params.get("membership");
    setMembership(membershipParam || "basic");
  }, [location.search]);

  if (success) {
    return (
      <>
        <p>Aktivoi tunnuksesi sähköpostista löytyvästä linkistä.</p>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            pt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
            sx={{ mr: 1 }}
          >
            Kirjaudu
          </Button>
        </Box>
      </>
    );
  } else {
    return (
      <UserRoot>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema[currentStep - 1]}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <ErrorAutoClear onClear={() => error && setError(null)} />
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Step1 currentStep={currentStep} />
              <Step2 currentStep={currentStep} />

              <div>
                <Button
                  type={"submit"}
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  {isLastStep ? "Lähetä" : "Seuraava"}
                </Button>
                {currentStep > 1 && (
                  <Button
                    style={{ marginRight: "8px" }}
                    type={"button"}
                    color="primary"
                    onClick={prevStep}
                    fullWidth
                  >
                    Takaisin
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </UserRoot>
    );
  }
}

export default SignUp;
